/*
 * Whether a person is here right now.
 *
 * Presence is ephemeral: it belongs to whoever holds the socket, it is never stored, and core has
 * neither a field nor an event for it. The module that does own it offers the reader as
 * `provides.presence`, the boot installs it here once (`core/modules/bind.ts`) — the same shape as
 * `core/shell/navigate.ts`, which is handed the router's push by the shell — and every surface that
 * draws a person asks this hook instead of core reaching up into a module.
 *
 * Without an installed reader nobody is anywhere: a build whose presence producer is switched off
 * draws no dots at all, which is exactly what a reader should see when there is nothing to know.
 *
 * The one place an observation meets a claim is the dot, and `withAway` below is where that meeting
 * is decided — once, for every surface that draws one.
 */

/** Here now, or connected but idle. Absent means "nothing is known", never "offline". */
export type Presence = "online" | "away"

/**
 * How a surface learns a person's state. It is a hook, not a getter: the reader subscribes to the
 * model it reads, so a person going away repaints the rows that show them. It is installed once,
 * before the first render, and never swapped afterwards — the hook it calls has to keep its place
 * in the render's hook order.
 */
export type PresenceReader = (userId: number) => Presence | undefined

const nobodyIsAnywhere: PresenceReader = () => undefined

let readPresence: PresenceReader = nobodyIsAnywhere

/** Installs the reader at boot; `null` puts the silent one back (the tests' way out). */
export function setPresenceSource(reader: PresenceReader | null): void {
	readPresence = reader ?? nobodyIsAnywhere
}

/** The state of one person, or undefined for nobody, for an unknown account, or with no reader. */
export function usePresence(userId: number | undefined): Presence | undefined {
	return readPresence(userId ?? 0)
}

/**
 * The dot a reader actually sees: what the transport observed, unless this person has said they are
 * away, in which case they are.
 *
 * A claim beats an observation because it is the more recent statement about the same thing — a
 * person who ticks *Show me as away* is at the keyboard while they tick it, and a dot that goes on
 * saying *Online* makes the switch useless. The claim also stands on its own: somebody who set it
 * and then closed the laptop is away to every reader, because the flag is stored and the connection
 * is not.
 *
 * Nothing of this reaches the server. Presence stays computed from live connections alone (the hub
 * knows no account column), so the fallback mail still decides on a connection rather than on a
 * claim, and only the picture a reader is shown is merged here.
 *
 * The claim arrives as the bare flag rather than as a whole status, because that is all this rule
 * looks at and because it outlives the status it was set with: the expiry job clears the emoji, the
 * line and the end time and leaves `statusAway` standing (`status.ts`).
 */
export function withAway(
	observed: Presence | undefined,
	away: boolean | undefined,
): Presence | undefined {
	return away === true ? "away" : observed
}
